import {testIdProps} from '@github-ui/test-id-props'
import {ArrowLeftIcon, ArrowRightIcon, IterationsIcon, PlusIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useState} from 'react'

import {ADD_BUTTON_ICON_WIDTH, ADD_BUTTON_MARGIN, ADD_BUTTON_WIDTH} from '../../../components/roadmap/constants'
import {useRoadmapDateFieldsMenu} from '../../../features/roadmap/roadmap-date-fields-menu'
import {not_typesafe_nonNullAssertion} from '../../../helpers/non-null-assertion'
import type {RoadmapColumn, TimeSpan} from '../../../helpers/roadmap-helpers'
import {useRoadmapSettings, useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {isIterationColumnModel} from '../../../models/column-model/guards'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {useNavigate} from '../../../router'
import {useProjectRouteParams} from '../../../router/use-project-route-params'
import {PROJECT_SETTINGS_FIELD_ROUTE} from '../../../routes'
import {RoadmapResources} from '../../../strings'
import {formatDateUtc} from '../date-utils'
import {
  PillAreaFocusType,
  type RoadmapPillAreaFocusWrapperProps,
  useRoadmapPillAreaFocus,
} from '../hooks/use-roadmap-pill-area-focus'
import {useUpdateRoadmapDates} from '../hooks/use-update-roadmap-dates'
import {
  useRoadmapGetTimeSpan,
  useRoadmapGetViewport,
  useRoadmapNavigation,
  useRoadmapView,
} from '../roadmap-view-provider'
import {dateFieldsConfigured, getTooltipText} from '../tooltip-helper'
import styles from './roadmap-pill-buttons.module.css'

/**
 * Renders an arrow button to navigate/scroll to the roadmap pill if it is out of view.
 */
type NavigationArrowProps = {
  isBefore: boolean
  startDate?: Date
  endDate?: Date
  role?: React.AriaRole
  wrapperProps?: RoadmapPillAreaFocusWrapperProps
  onFocusInternalElement?: React.FocusEventHandler
  arrowButtonRef?: React.RefObject<HTMLButtonElement | null>
  className?: string
}

export const NavigationArrow = memo(function NavigationArrow({
  isBefore,
  startDate,
  endDate,
  role,
  wrapperProps,
  onFocusInternalElement,
  arrowButtonRef,
  className,
}: NavigationArrowProps) {
  const {scrollToDate} = useRoadmapNavigation()
  const getViewport = useRoadmapGetViewport()
  const tableWidth = useRoadmapTableWidth()
  const viewport = getViewport()

  const onArrowClick = useCallback(() => {
    if (startDate) {
      scrollToDate(startDate, true)
    }
  }, [scrollToDate, startDate])

  const tooltip = startDate
    ? RoadmapResources.scrollToDateText(startDate)
    : endDate
      ? RoadmapResources.scrollToDateText(endDate)
      : ''

  return (
    <div
      role={role}
      // Separate from sx to avoid recomputing the `sx` CSS class when the table width changes
      style={{
        left: isBefore
          ? tableWidth + ADD_BUTTON_MARGIN
          : tableWidth + (viewport.right - viewport.left) - ADD_BUTTON_WIDTH,
      }}
      className={clsx(styles.Box)}
      {...(wrapperProps ? wrapperProps : {})}
    >
      <IconButton
        tooltipDirection={isBefore ? 'e' : 'w'}
        onClick={onArrowClick}
        variant="invisible"
        icon={isBefore ? ArrowLeftIcon : ArrowRightIcon}
        {...testIdProps('roadmap-navigation-button')}
        aria-label={tooltip}
        ref={arrowButtonRef ?? null}
        className={clsx(className, styles.defaultButton)}
        {...(onFocusInternalElement ? {onFocus: onFocusInternalElement} : {})}
      />
    </div>
  )
})

export const NavigationArrowWithFocusContext = memo(function NavigationArrowWithFocusContext(props: {
  isBefore: boolean
  startDate?: Date
  endDate?: Date
  role?: React.AriaRole
}) {
  const {wrapperProps, onFocusInternalElement, arrowButtonRef, focusType} = useRoadmapPillAreaFocus()
  return (
    <NavigationArrow
      wrapperProps={wrapperProps}
      onFocusInternalElement={onFocusInternalElement}
      arrowButtonRef={arrowButtonRef}
      className={focusType === PillAreaFocusType.ArrowButton ? 'is-focused' : undefined}
      {...props}
    />
  )
})

type AddDateButtonProps = {
  /** The item to add a date to. */
  item: MemexItemModel
  /** The target date to add or use to lookup the corresponding iteration value. If undefined, add to today. */
  date: Date | undefined
  /** The x offset for the button in the roadmap, if hovered. */
  offsetX: number
  /** Whether this is the default, non-hovered button to add to today.  Supports keyboard use on focus. */
  isDefaultAddButton?: boolean
  /** Optionally suppress showing the tooltip */
  hideTooltip?: boolean
  /** Optionally suppress showing the placeholder for adding */
  hidePlaceholder?: boolean
  errorMessage?: string
}

/**
 * Renders an Add date/iteration + button with variable styling/behavior based on hover or default button positioning
 */
export const AddDateButton = memo(function AddDateButton({
  item,
  date,
  offsetX,
  isDefaultAddButton,
  hideTooltip,
  hidePlaceholder,
  errorMessage,
}: AddDateButtonProps) {
  const getTimeSpan = useRoadmapGetTimeSpan()
  const {today} = useRoadmapView()
  const {updateItemDates} = useUpdateRoadmapDates()
  const {dateFields} = useRoadmapSettings()
  const tableWidth = useRoadmapTableWidth()
  const {setOpenDateConfigurationMenu} = useRoadmapDateFieldsMenu()
  const navigate = useNavigate()
  const {wrapperProps, onFocusInternalElement, addDateButtonRef, focusType} = useRoadmapPillAreaFocus()
  const projectRouteParams = useProjectRouteParams()
  const hasDateFields = dateFieldsConfigured(dateFields)
  const addToToday = isDefaultAddButton || !date
  const targetDate = addToToday ? today : date
  const timeSpan = getTimeSpan(targetDate)
  const isMissingIteration = !timeSpan.start || !timeSpan.end
  const disableAddButton = !!errorMessage

  let tooltip: string | undefined
  if (errorMessage) {
    tooltip = errorMessage
  } else if (isMissingIteration) {
    tooltip = RoadmapResources.noIterationConfiguredTooltip(formatDateUtc(targetDate))
  } else if (hasDateFields) {
    tooltip = getTooltipText(timeSpan)
    tooltip = addToToday ? `Add to today at ${tooltip}` : `Add to ${tooltip}`
  } else {
    tooltip = RoadmapResources.noDateFieldsConfiguredTooltip
  }

  const onClick = useCallback(() => {
    if (!hasDateFields) {
      setOpenDateConfigurationMenu(true)
    } else if (isMissingIteration) {
      const iterationField = getIterationField(
        !timeSpan.start ? not_typesafe_nonNullAssertion(dateFields[0]) : not_typesafe_nonNullAssertion(dateFields[1]),
      )
      if (iterationField) {
        navigate({
          pathname: PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
            ...projectRouteParams,
            fieldId: iterationField.id,
          }),
        })
      }
    } else if (targetDate) {
      updateItemDates(item, timeSpan)
    }
  }, [
    dateFields,
    hasDateFields,
    isMissingIteration,
    item,
    navigate,
    setOpenDateConfigurationMenu,
    targetDate,
    timeSpan,
    updateItemDates,
    projectRouteParams,
  ])

  const [isFocused, setIsFocused] = useState<boolean>(false)

  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(true)
      if (onFocusInternalElement) onFocusInternalElement(e)
    },
    [onFocusInternalElement],
  )

  const onBlur = useCallback(() => setIsFocused(false), [])
  const showPlaceholder = !hidePlaceholder && !(isDefaultAddButton && !isFocused) && !isMissingIteration
  const icon = (isDefaultAddButton && !isFocused) || !isMissingIteration ? PlusIcon : IterationsIcon
  const {className, style} = isDefaultAddButton
    ? {
        className: styles.addButtonContainer,
        style: {left: tableWidth + ADD_BUTTON_MARGIN},
      }
    : {
        className: clsx(styles.buttonContainer, hideTooltip && styles.hideTooltip),
        style: {left: offsetX - ADD_BUTTON_ICON_WIDTH / 2},
      }

  return (
    <>
      {showPlaceholder && <PillPlaceholder timeSpan={timeSpan} />}
      <div className={className} style={style} role="gridcell" {...wrapperProps}>
        <IconButton
          // We are using the tooltip as a rendering hint here because we want to ensure that the tooltip position
          // gets updated whenever the actively hovered day changes - otherwise it may lag behind the cursor
          key={tooltip}
          tooltipDirection={hasDateFields && !isMissingIteration && !addToToday ? 'n' : 'e'}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          variant="invisible"
          icon={icon}
          {...testIdProps('roadmap-add-date-button')}
          className={clsx(
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            'roadmap-add-date-button',
            focusType === PillAreaFocusType.AddDateButton && 'is-focused',
            styles.defaultButton,
            addToToday || isMissingIteration ? null : styles.hoverButton,
          )}
          aria-label={tooltip}
          ref={addDateButtonRef}
          inactive={disableAddButton}
        />
      </div>
    </>
  )
})

/**
 * Renders a placholder time span indicating where a pill will be added based on the date/iteration
 */
const PillPlaceholder = ({timeSpan}: {timeSpan: TimeSpan}) => {
  const {getX, columnWidth} = useRoadmapView()
  const spanStartLeftOffset = timeSpan.start ? getX(timeSpan.start) : 0
  const spanEndLeftOffset = timeSpan.end ? getX(timeSpan.end) : 0

  return (
    <div
      role="gridcell"
      style={{
        width: spanEndLeftOffset - spanStartLeftOffset + columnWidth,
        left: spanStartLeftOffset,
      }}
      className={clsx(styles.ghostPill)}
    />
  )
}

function getIterationField(column: RoadmapColumn) {
  if (column && column !== 'none' && isIterationColumnModel(column)) {
    return column
  }
}
